import { ComponentType, useEffect, useState, useCallback } from 'react';
import { Country, CountryCode } from '../../api';
import { useEventListener } from '../hooks/useEventListener';
import { getDefaultLanguageCode } from '../hooks/useLanguages';
import { makeLanguageOptions } from '../utils/makeOptions';
import { Option, SelectChangeEvent, HOCSelectProps } from './types';

interface LanguageSelectWrapperProps extends HOCSelectProps {
  Component: ComponentType<any>;
  countries: Country[];
  loading: boolean;
  error: Error | undefined;
}
const LanguageSelectWrapper = ({
  onError, onLoading, onChange, defaultCountry, countries, loading, error, Component, value, ...rest
}: LanguageSelectWrapperProps) => {
  const [country, setCountry] = useState<CountryCode | undefined>(defaultCountry);
  const [options, setOptions] = useState<Option[]>([]);
  const [selectedValue, setSelectedValue] = useState<string>(value || '');

  const changeLanguage = useCallback((language: string) => {
    if (options.length > 0) {
      onChange?.({ target: { value: language } });
      setSelectedValue(language);
    }
  }, [onChange, options]);

  const handleCountrySelected = (e: CustomEvent<{ countryCode: string }>) => {
    setSelectedValue('');
    setCountry(e.detail.countryCode);
  }

  useEventListener('mist-com-country-selected', handleCountrySelected as EventListener);

  useEffect(() => {
    if (countries.length > 0 && country) {
      setOptions(makeLanguageOptions(countries, country));
    } else if (countries.length > 0) {
      setCountry(countries[0].code);
    }
  }, [countries, country]);

  useEffect(() => {
    if (selectedValue === '') {
      const countryDefaultLanguage = getDefaultLanguageCode(countries, country);
      changeLanguage(countryDefaultLanguage);
    }
  }, [countries, country, changeLanguage, selectedValue]);

  useEffect(() => onLoading?.(loading), [loading, onLoading]);
  useEffect(() => onError?.(error), [error, onError]);

  const handleChange = (e: SelectChangeEvent<string>) => changeLanguage(e.target.value);

  return <Component {...rest}
    // Hack for ecom selects, if options are empty array it crashes
    options={!options || options.length === 0 ? [{ label: '', value: '' }] : options}
    onChange={handleChange}
    value={selectedValue}
  />;
}

export default LanguageSelectWrapper;
